import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useEffect } from 'react';

// Initialize Sentry
export const initSentry = () => {
  const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true';
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
  const release = import.meta.env.VITE_APP_VERSION || 'oneportal-shell@1.0.0';

  // Check if Sentry is enabled
  if (!sentryEnabled) {
    console.log('Sentry disabled via VITE_SENTRY_ENABLED flag.');
    return;
  }

  // Only initialize if DSN is provided
  if (!sentryDsn) {
    console.warn('Sentry DSN not configured. Error monitoring disabled.');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment,
    release,

    // Performance Monitoring
    integrations: [
      // Browser profiling integration
      Sentry.browserProfilingIntegration(),
      
      // React Router integration for navigation tracking
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      
      // Replay integration for session replay
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring sample rates
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev
    profilesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev

    // Session replay sample rates
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0, // Always capture replay on error

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      
      // Network errors that are expected
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      
      // Auth0 expected errors
      'consent_required',
      'login_required',
    ],

    // Don't send PII
    beforeSend(event, hint) {
      // Filter out Auth0 tokens from error data
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
      }
      
      // Remove sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.data?.token || breadcrumb.data?.authorization) {
            return {
              ...breadcrumb,
              data: {
                ...breadcrumb.data,
                token: '[Filtered]',
                authorization: '[Filtered]',
              },
            };
          }
          return breadcrumb;
        });
      }

      return event;
    },

    // Set custom context
    beforeSendTransaction(event) {
      event.contexts = {
        ...event.contexts,
        app: {
          name: 'OnePortal',
          version: release,
        },
      };
      return event;
    },
  });

  console.log(`✅ Sentry initialized (${environment})`);
};

// Helper function to set user context
export const setSentryUser = (user: { id: string; email?: string; username?: string }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
};

// Helper function to clear user context (on logout)
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

// Helper function to capture custom errors
export const captureError = (error: Error, context?: Record<string, any>) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

// Helper function to add breadcrumb
export const addBreadcrumb = (message: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message,
    data,
    level: 'info',
  });
};
